import React, { FC, PropsWithChildren, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';

import { useScrollShadow } from 'Src/components/useScrollShadow';
import { CheckItemVoidReasonModal } from 'Src/feature/check/CheckItemVoidReasonModal';
import { Check, CheckItem } from 'Src/feature/check/models';
import { CheckItemDiscountModal } from 'Src/feature/check-item-discount/CheckItemDiscountModal';
import { CheckItemSeatSelection } from 'Src/feature/seat-assignment/CheckItemSeatSelection';
import { HotKeyCheckItemButtons } from 'Src/feature/seat-assignment/HotKeyCheckItemButtons';
import { CheckItemButtonPropsWithUID, useSeatAssignment } from 'Src/feature/seat-assignment/useSeatAssignment';
import useRoute from 'Src/routes/useRoute';

import { CheckItemButton, CheckItemButtonProps } from './CheckItemButton';
import { CheckItemScheduleModal } from './CheckItemScheduleModal';
import { useCheckItemAutoScroll } from './useCheckItemAutoScroll';
import { useCheckItemBuilder } from './useCheckItemBuilder';
import { useCheckItemName } from './useCheckItemName';
import { UsedCurrentCheckItemButtons } from './useCurrentCheckItemButtons';

import styles from './CurrentCheckItemButtons.module.scss';

const sectionId = 'CurrentCheckItemButtons';
const NEW_CHECK_UUID = 'new-check-item' as const;

interface CurrentCheckItemButtonsProps {
  store: UsedCurrentCheckItemButtons;
  hasNewCheckItem: boolean;
}

const CurrentCheckItemButtons: FC<PropsWithChildren<CurrentCheckItemButtonsProps>> = ({
  store,
  hasNewCheckItem,
  children,
  ...rest
}) => {
  const checkItemBuilder = useCheckItemBuilder();
  const { getCheckItemName } = useCheckItemName();
  const seatAssignment = useSeatAssignment();
  const { route, crumbs } = useRoute();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  useScrollShadow({ targetRef: scrollContainerRef, mode: 'vertical' });

  const checkItemButtonProps = useMemo(() => {
    let props: CheckItemButtonPropsWithUID[] = [];

    const itemToPropsMapper = (_checkItem: typeof store.activeCheckItems[number]) => {
      const printJob = store.checkPrints.all.map.get(_checkItem.id);
      const printState = printJob?.states.combined.state;

      let holdUntil: string | undefined = undefined;
      if (printState === 'Scheduled' && printJob?.print.dateTime && printJob?.print.dateTime?.toMillis() > DateTime.now().plus({ seconds: 5 }).toMillis()) {
        holdUntil = printJob?.print.dateTime?.toFormat('hh:mm a');
      }
      let sendAt: number | undefined = undefined;
      if (_checkItem.sendSchedule?.sendAt && (printState === 'Scheduled' || !printState)) {
        sendAt = _checkItem.sendSchedule.sendAt;
      }

      // merge check item state w/ check item print state
      // - check item print state is a collection of multiple states (check item and check item modifier print states)
      const is: CheckItemButtonProps['is'] = {
        loading: store.isProcessing || printState === 'Scheduled' || printState === 'Progress',
        hold: _checkItem.status === 'IndefiniteHold',
        scheduled: !!_checkItem.sendSchedule?.sendAt,
        error: printState === 'Failed',
        sent: printState === 'Completed',
        readonly: store.mode === 'readonly',
        // might not be needed but carry over from previous use
        // - should not need to wrap in "Boolean" function
        voided: CheckItem.IsVoid(store.activeCheck as Check, _checkItem),
        // FYI: some check item values will be editing single property edit or a bulk property edit
        // - single property edits:
        //  - require a single request/trip to the server (save and forget)
        //  - these edits will update the checkItem model used in this component
        //  - these include:
        //    - discounts
        //    - void
        //    - delete
        //    - repeat
        //    - hold
        //    - edit price (price variant)
        //    - split check item by seat position
        //    - split check item by quantity
        // - bulk property edits:
        //  - can be bulk saved to the server
        //  - these include:
        //    - quantity
        //    - seat position
        //    - notes
        //    - modifiers
        // "someSelections"
        // - when one or more check items has been selected
        someSelections: store.isSomeSelected,
        selected: false,
      };

      is.selected = store.isCheckItemIdSelected(_checkItem.id);

      const discounts: CheckItemButtonProps['discounts'] = (_checkItem.discounts || []).filter((d) => d.status == 'Enabled');

      let modifiers: CheckItemButtonProps['modifiers'] = undefined;

      if (_checkItem.modifiers && _checkItem.modifiers.length) {
        modifiers = _checkItem.modifiers?.map((modifier) => {
          let name = modifier.name || modifier.groupName;
          // FIXME...
          // - currently unable to determine a
          //   command palette initiated "ADD" command or the default ModifierOption that is always "ADD"
          // - the add commands will show up while using the command palette but will not show when saved to the Check
          if (modifier.cmdName && modifier.cmdName.toLowerCase() !== 'add') {
            if (modifier.type === 'prefix') {
              name = `${modifier.cmdName} ${name}`;
            } else {
              name = `${name} ${modifier.cmdName}`;
            }
          }

          return {
            name,
            cost: modifier.price ? modifier.price : undefined
          };
        });
      }

      const prop = {
        uuid: _checkItem.id,
        discounts,
        is,
        holdUntil,
        sendAt,
        modifiers,
        name: getCheckItemName({ checkItem: _checkItem, menuItemName: _checkItem.menuItem.combinedName }),
        onSelectOne: () => store.onCheckItemSingleSelect(_checkItem.id),
        onSelectMulti: () => store.onCheckItemMultiSelect(_checkItem.id),
        quantity: _checkItem.quantity || 1,
        seatNames: _checkItem.seatNames,
        gross: _checkItem.gross,
        grossLessItemDiscount: _checkItem.grossLessItemDiscount,
        note: _checkItem.note,
      };

      // props that come from the check item builder form
      if (checkItemBuilder && checkItemBuilder.source.checkItem?.id === _checkItem.id) {
        prop.quantity = checkItemBuilder.built.quantity || 1;
        prop.seatNames = checkItemBuilder.built.seatNames;
        prop.gross = checkItemBuilder.built.price || 0;
      }
      return prop;
    };

    if (seatAssignment.type === 'UseHotkeys') {
      props = store.sortedActiveCheckItemsWithSeats.asArray.map(itemToPropsMapper);
    } else {
      props = store.sortedActiveCheckItems.asArray.map(itemToPropsMapper);
    }

    // add an additional check item button when we are in a new state
    // - pull values from the checkItemBuilder
    if (hasNewCheckItem) {
      // strict equality of this array to recalc after state changes
      props.push({
        uuid: NEW_CHECK_UUID,
        discounts: undefined,
        is: {
          // selection state
          selected: true,
          readonly: false,
          // check item status state
          sent: false,
          voided: false,
          hold: false,
          scheduled: false,
          loading: false,
          error: false,
          someSelections: true,
        },
        name: checkItemBuilder.source.menuItem?.combinedName || '',
        quantity: checkItemBuilder.built.quantity || 1,
        seatNames: checkItemBuilder.built.seatNames,
        gross: checkItemBuilder.built.price || 0,
        grossLessItemDiscount: checkItemBuilder.built.price || 0,
        // new CheckItem modifiers is not meant to render in the current check panel
        modifiers: [],
      });
    }
    return props;
  }, [checkItemBuilder, getCheckItemName, hasNewCheckItem, seatAssignment.type, store]);

  const isMultipleSelected = useMemo(() => {
    const selectedAmount = checkItemButtonProps.filter(({ is }) => is.selected).length;
    return selectedAmount > 1;
  }, [checkItemButtonProps]);

  const isPaymentRoute = route.is(crumbs.pos.check.payment.main);

  useCheckItemAutoScroll({
    scrollElementRef: scrollContainerRef,
    scrollContentRef: scrollContentRef,
    checkItemUUIDs: checkItemButtonProps.map(({ uuid }) => uuid),
    isOff: isPaymentRoute || isMultipleSelected,
  });

  // show/hide the "UseHotkeys" seat selector
  const showCheckItemSeatSelector = useMemo(() => {
    // should not show on a payment route
    if (isPaymentRoute) return false;
    // should not show when multiple check items are selected
    if (isMultipleSelected) return false;
    return seatAssignment.type === 'UseHotkeys';
  }, [isMultipleSelected, isPaymentRoute, seatAssignment.type]);

  return (
    <>
      <div className={styles.root} data-testid={sectionId} {...rest}>
        {showCheckItemSeatSelector && <CheckItemSeatSelection />}
        <div ref={scrollContainerRef} className={styles.scrollContainer}>
          <div ref={scrollContentRef} className={styles.content}>
            {seatAssignment.type === 'UseHotkeys'
              ? <HotKeyCheckItemButtons checkItemButtonProps={checkItemButtonProps} isPaymentRoute={isPaymentRoute} />
              : checkItemButtonProps.map(({ uuid, ...props }) => (
                <CheckItemButton key={uuid} uuid={uuid} {...props} />
              ))}
            {children}
          </div>
        </div>
      </div>
      <CheckItemVoidReasonModal
        open={store.modals.voidReason.isOpen}
        onVoid={store.onSaveCheckItemVoidReason}
        onClose={store.modals.voidReason.onClose}
        form={store.voidItemForm}
        reasonOptions={store.voidReasonOptions ?? []}
      />
      <CheckItemScheduleModal
        open={store.modals.schedule.isOpen}
        onSave={store.onSaveCheckItemSchedule}
        onClose={store.modals.schedule.onClose}
        form={store.scheduleItemForm}
        checkItemName={store.modals.schedule.item?.menuItem.name ?? ''}
      />
      <CheckItemDiscountModal
        open={store.modals.discount.isOpen}
        onDone={store.modals.discount.onDone}
        check={store.activeCheck}
        checkItemIds={store.selectedCheckItemIds}
      />
    </>
  );
};

export { CurrentCheckItemButtons, sectionId };
export type { CurrentCheckItemButtonsProps };
